import { useCallback, useState, useRef, useEffect } from 'react';
import { throttle } from 'app/helpers/throttle';
import { t } from 'i18next';
import { TargetCatalogEntryInfo } from 'ais-facade-api/api';
import { InfoWrapper } from 'app/components';
import { FormikErrors } from 'formik';
import { FilterFormData } from './FilterPanel';
import { useDebounce } from 'app/hooks/useDebounce';
import { AutoComplete, SelectedOptionI } from '../AutoComplete';
import { useIntersectionObserver } from '@siberiacancode/reactuse';
import { useGetVesselCatalogMutation } from 'app/api/facade';
import styles from './FilterPanel.module.scss';

type VesselFieldT = 'mmsi';

interface VesselFieldI {
  fieldName: VesselFieldT;
  onSelect: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<FilterFormData>>;
}
export const VesselField = ({ fieldName, onSelect }: VesselFieldI) => {
  const [getCatalog, { data: vesselsData, ...catalogRequest }] = useGetVesselCatalogMutation({
    fixedCacheKey: 'get-vessels-catalog-mutation',
  });

  const inputRef = useRef<HTMLInputElement | null>(null);

  const debouncedGetCatalogHandler = useCallback(throttle(getCatalog, 500), []);

  const getVessels = (term: string, offset: number) => {
    if (term.length < 2) {
      debouncedGetCatalogHandler({
        filter: {},
        order: {
          direction: 'ASC',
          by: 'NAME',
        },
        pagination: {
          offset: offset,
          limit: 10,
        },
      });
    }

    if (term.length >= 2) {
      debouncedGetCatalogHandler({
        filter: {
          term: term,
        },
        order: {
          direction: 'ASC',
          by: 'NAME',
        },
        pagination: {
          offset: offset,
          limit: 10,
        },
      });
    }
  };

  const [offset, setOffset] = useState<number>(0);
  const [vessels, setVessels] = useState<{ vessels: TargetCatalogEntryInfo[] }>({ vessels: [] });
  const [inputValue, setInputValue] = useState<string>('');
  const [open, setOpen] = useState(false);
  const debouncedValue = useDebounce<string>(inputValue, 500);
  const [options, setOptions] = useState<
    | {
        value?: string;
        label?: string | JSX.Element;
        placeholder?: string;
      }[]
    | null
  >(null);

  const handleOpen = useCallback(() => {
    setOpen(true);
    if (inputRef.current?.value) {
      getVessels(inputRef.current?.value, 0);
    }

    if (catalogRequest.isUninitialized) {
      setOffset(0);
      setVessels({ vessels: [] });
      getVessels('', 0);
    }
    if (!debouncedValue) {
      setVessels({ vessels: [] });

      getVessels('', 0);
    }
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  const { ref: scrollableDivRef } = useIntersectionObserver<HTMLDivElement>({
    threshold: 1,
    onChange: (entry) => {
      if (entry.isIntersecting) setOffset((prev) => prev + 10);
    },
  });

  const handleSelect = (selectedOption?: SelectedOptionI | null) => {
    if (!selectedOption?.value) {
      onSelect(fieldName, '');
    } else {
      const vessel = vessels.vessels.filter((vessel: TargetCatalogEntryInfo) => {
        return String(vessel.mmsi) === selectedOption?.value;
      })?.[0];
      if (vessel) {
        onSelect(fieldName, vessel.mmsi?.toString());
      }
    }
    handleClose();
  };

  useEffect(() => {
    if (vesselsData && vesselsData.results && vesselsData.results?.length > 0) {
      setVessels((prev) => {
        const prevVessels = prev.vessels;
        const newVessels = vesselsData.results?.filter((vessel) => {
          return !prevVessels.some((prevVessel) => prevVessel.mmsi === vessel.mmsi);
        });
        return {
          vessels: [...prevVessels, ...(newVessels || [])],
        };
      });
    }
  }, [vesselsData]);

  useEffect(() => {
    const options = vessels?.vessels.map((vessel) => ({
      value: String(vessel.mmsi),
      placeholder: vessel.registryData?.name || String(vessel.mmsi),
      label: (
        <InfoWrapper
          label={!vessel.registryData?.name ? `${vessel.mmsi}` : `${vessel.registryData.name} (${vessel.mmsi})`}
          dots={false}
        />
      ),
    }));
    setOptions(options);
  }, [vessels]);

  useEffect(() => {
    if (inputRef?.current) {
      setInputValue(inputRef.current.value);
    }
  }, [inputRef?.current?.value]);

  useEffect(() => {
    setOffset(0);
    catalogRequest.reset();

    if (debouncedValue) {
      setVessels({ vessels: [] });
    }

    if (!catalogRequest.isUninitialized && open) {
      getVessels(debouncedValue, 0);
    }
  }, [debouncedValue]);

  useEffect(() => {
    if (!catalogRequest.isUninitialized && open) {
      getVessels(debouncedValue, offset);
    }
  }, [offset]);

  return (
    <AutoComplete
      initialValue=""
      className={styles.vessel_input}
      id={`autocomplete-${fieldName}`}
      placeholder={t('SelectVessel')}
      staticSearch={false}
      onChange={setInputValue}
      inputRef={inputRef}
      divRef={scrollableDivRef}
      loading={catalogRequest.isLoading}
      options={options}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      onSelect={handleSelect}
      isSuccess={catalogRequest.isSuccess}
      hasmore={vesselsData?.results?.length !== 0}
    />
  );
};
